import { HistoryOutlined, LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { useHotkey } from "@tanstack/react-hotkeys";
import { ConfigProvider, Dropdown, Flex, Grid, Input, List, Typography } from "antd";
import React, { useCallback, useEffect, useRef, useState, useContext } from "react";
import VirtualList from "rc-virtual-list";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGlobalSearch from "../hooks/useGlobalSearch.jsx";
import { AppContext } from "../../context/AppContext.js";
import "../css/GlobalSearchBar.css";
import {
    getGlobalSearchRecentSearches,
    addGlobalSearchRecentSearchIntoLocalStorage,
    buildGlobalSearchResultDescription,
    highlightMatchingText,
    getPathForTheSelectedResult,
    getGlobalSearchNavigationState,
} from "../utils/globalSearchUtils.js";
import {
    CATEGORY_OPTIONS,
    EMPTY_STATE_MESSAGE,
    CATEGORY_LABELS,
    CATEGORY_ICONS_CLASSNAME,
    GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY,
} from "../constants/globalSearchConstants.js";
import { isMac } from "../../pages/utils/commonUtils.js";

const { Text } = Typography;

const GLOBAL_SEARCH_RESULT_ITEM_HEIGHT = 80;
const GLOBAL_SEARCH_DROPDOWN_FOOTER_HEIGHT = 30;
const GLOBAL_SEARCH_DROPDOWN_HEIGHT = 530;

const GlobalSearchDropdownFooter = () => (
    <div className="hs-bg-light-grey-5 hs-border-top-light hs-h-40-px hs-fs-11 hs-color-placeholder-grey d-flex align-items-center px-2">
        <div className="ms-auto m-2">
            <span className="hs-border-light-grey-2 p-1 hs-color-dark-grey-2 rounded-1">{"esc"}</span> to close
        </div>
    </div>
);

const GlobalSearchResultRow = ({ globalSearchResultObject, activeCategory, globalSearchQuery, handleGlobalSearchResultClick }) => {
    if (!globalSearchResultObject) return null;
    const categoryIconClassName = CATEGORY_ICONS_CLASSNAME?.[globalSearchResultObject?.type];
    const globalSearchResultDescriptionText = buildGlobalSearchResultDescription(globalSearchResultObject);
    const globalSearchResultDisplayTitle =
        globalSearchResultObject?.title ||
        CATEGORY_LABELS?.[globalSearchResultObject?.type] ||
        globalSearchResultObject?.type ||
        "";
    const shouldShowCategoryIconInTheResult = activeCategory === GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY;

    return (
        <Flex
            className="cursor-pointer overflow-hidden px-3 py-2"
            align="flex-start"
            gap={8}
            onClick={() => handleGlobalSearchResultClick(globalSearchResultObject)}
        >
            {shouldShowCategoryIconInTheResult && (
                <div className={`actionIconsSprite2 global-search-result-type-icon ${categoryIconClassName}`} />
            )}
            <div>
                <div className="fw-medium">{highlightMatchingText(globalSearchResultDisplayTitle, globalSearchQuery)}</div>
                <div className="hs-fs-12 hs-color-light-grey-2 mt-1">{globalSearchResultDescriptionText}</div>
            </div>
        </Flex>
    );
};

const GlobalSearchCategoryPanel = ({
    appProperties,
    activeCategory,
    setActiveCategory,
    globalSearchResults,
    globalSearchQuery,
    clearGlobalSearchResults,
    setIsGlobalSearchDataWasFetching,
}) => {
    const categoryOptionsNeedToBeVisible = () => {
        if (!appProperties?.controller?.contactsEnabled) {
            return CATEGORY_OPTIONS?.filter((categoryOption) => categoryOption?.key !== "contacts");
        }
        return CATEGORY_OPTIONS;
    };

    return (
        <Flex gap={10} vertical className="hs-border-right-light px-3 py-3">
            {categoryOptionsNeedToBeVisible().map((category) => {
                const categoryIconClassName = category?.iconClassName;
                return (
                    <div
                        key={category?.key}
                        className={`cursor-pointer p-1 bg-transparent w-100 hs-fs-13 hs-color-dark-grey-2 text-start hs-border-solid-transparent ${activeCategory === category.key ? "rounded-2 hs-border-light-grey-2" : ""}`}
                        onClick={() => {
                            if (activeCategory !== category?.key) {
                                if (globalSearchResults?.length > 0) clearGlobalSearchResults(true);
                                setActiveCategory(category?.key);
                            }
                            if (globalSearchQuery?.trim()?.length >= 2) {
                                setIsGlobalSearchDataWasFetching(true);
                            }
                        }}
                    >
                        <Flex align="center" justify="flex-start" gap={3} className="p-1">
                            <div className={`actionIconsSprite-2 ${categoryIconClassName} me-1`}></div>
                            {category?.label}
                        </Flex>
                    </div>
                );
            })}
        </Flex>
    );
};

const GlobalSearchResultsPanel = ({
    isGlobalSearchDataWasFetching,
    globalSearchResults,
    globalSearchQuery,
    globalSearchRecentSearches,
    handleGlobalSearch,
    activeCategory,
    handleGlobalSearchResultClick,
    isMoreDataPresentInPagination,
    isGlobalSearchFetchingDataByPagination,
    loadMoreGlobalSearchResults,
}) => {
    const globalSearchResultsListHeight = GLOBAL_SEARCH_DROPDOWN_HEIGHT - GLOBAL_SEARCH_DROPDOWN_FOOTER_HEIGHT;

    const handleLoadMoreGlobalSearchResults = (event) => {
        if (!isMoreDataPresentInPagination || isGlobalSearchFetchingDataByPagination) return;
        const { scrollTop, scrollHeight } = event.currentTarget;
        const maxScroll = scrollHeight - globalSearchResultsListHeight;
        if (maxScroll < GLOBAL_SEARCH_RESULT_ITEM_HEIGHT) return;
        if (scrollTop > maxScroll - GLOBAL_SEARCH_RESULT_ITEM_HEIGHT * 4) {
            loadMoreGlobalSearchResults();
        }
    };

    return (
        <Flex vertical flex={1}>
            {isGlobalSearchDataWasFetching && globalSearchResults?.length === 0 ? (
                <div className="hs-color-placeholder-grey text-center d-flex align-items-center justify-content-center h-100">
                    Searching...
                </div>
            ) : globalSearchQuery?.trim()?.length < 2 ? (
                globalSearchRecentSearches?.length > 0 ? (
                    <Flex vertical>
                        <Text
                            type="secondary"
                            className="hs-fs-12 hs-border-bottom-light px-3 py-2 mb-0 fw-medium hs-color-placeholder-grey cursor-default d-block flex-shrink-0"
                        >
                            Recent searches
                        </Text>
                        <Flex vertical>
                            {globalSearchRecentSearches?.map((resentSearch, index) => (
                                <div
                                    key={`${resentSearch}-${index}`}
                                    className="cursor-pointer overflow-hidden d-flex align-items-start global-search-result-and-recent-search-row-hover px-3 py-2"
                                    onClick={() => handleGlobalSearch(resentSearch)}
                                >
                                    <HistoryOutlined className="hs-fs-14 hs-color-placeholder-grey me-3 mt-1" />
                                    <span className="text-break">{resentSearch}</span>
                                </div>
                            ))}
                        </Flex>
                    </Flex>
                ) : (
                    <Flex vertical align="center" justify="center" flex={1} className="text-center">
                        <Text strong>Search your workspace</Text>
                        <Text className="hs-color-placeholder-grey text-center">
                            Type to search across{" "}
                            {activeCategory === GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY ? EMPTY_STATE_MESSAGE.all : activeCategory}
                        </Text>
                    </Flex>
                )
            ) : globalSearchResults?.length === 0 ? (
                <div className="hs-color-placeholder-grey text-center d-flex align-items-center justify-content-center h-100">
                    No results found
                </div>
            ) : (
                <div className="h-100">
                    <List>
                        <VirtualList
                            data={globalSearchResults}
                            height={globalSearchResultsListHeight}
                            itemHeight={GLOBAL_SEARCH_RESULT_ITEM_HEIGHT}
                            itemKey="sourceId"
                            onScroll={handleLoadMoreGlobalSearchResults}
                        >
                            {(globalSearchResult) => (
                                <List.Item
                                    key={globalSearchResult?.sourceId}
                                    className="p-0 global-search-result-and-recent-search-row-hover"
                                >
                                    <GlobalSearchResultRow
                                        globalSearchResultObject={globalSearchResult}
                                        activeCategory={activeCategory}
                                        globalSearchQuery={globalSearchQuery}
                                        handleGlobalSearchResultClick={handleGlobalSearchResultClick}
                                    />
                                </List.Item>
                            )}
                        </VirtualList>
                    </List>
                </div>
            )}
        </Flex>
    );
};

const GlobalSearchDropdown = ({
    appProperties,
    activeCategory,
    globalSearchResults,
    setActiveCategory,
    clearGlobalSearchResults,
    setIsGlobalSearchDataWasFetching,
    globalSearchQuery,
    isGlobalSearchDataWasFetching,
    globalSearchRecentSearches,
    handleGlobalSearch,
    handleGlobalSearchResultClick,
    isMoreDataPresentInPagination,
    isGlobalSearchFetchingDataByPagination,
    loadMoreGlobalSearchResults,
}) => (
    <div className="global-search-dropdown hs-bg-white hs-border-dark-grey rounded-2 d-flex flex-column overflow-hidden mt-1">
        <Flex flex={1}>
            <GlobalSearchCategoryPanel
                appProperties={appProperties}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
                globalSearchResults={globalSearchResults}
                globalSearchQuery={globalSearchQuery}
                clearGlobalSearchResults={clearGlobalSearchResults}
                setIsGlobalSearchDataWasFetching={setIsGlobalSearchDataWasFetching}
            />
            <GlobalSearchResultsPanel
                isGlobalSearchDataWasFetching={isGlobalSearchDataWasFetching}
                globalSearchResults={globalSearchResults}
                globalSearchQuery={globalSearchQuery}
                globalSearchRecentSearches={globalSearchRecentSearches}
                handleGlobalSearch={handleGlobalSearch}
                activeCategory={activeCategory}
                handleGlobalSearchResultClick={handleGlobalSearchResultClick}
                isMoreDataPresentInPagination={isMoreDataPresentInPagination}
                isGlobalSearchFetchingDataByPagination={isGlobalSearchFetchingDataByPagination}
                loadMoreGlobalSearchResults={loadMoreGlobalSearchResults}
            />
        </Flex>
        <GlobalSearchDropdownFooter />
    </div>
);

const GlobalSearchInput = React.forwardRef(
    (
        { isGlobalSearchDataWasFetching, globalSearchQuery, handleGlobalSearch, handleOpenGlobalSearch },
        globalSearchInputRef
    ) => {
        const shortcutHintToOpenGlobalSearch = isMac() ? "⌘K" : "Ctrl+K";
        return (
            <Input
                ref={globalSearchInputRef}
                className="hs-z-index-1100 w-100 hs-fs-13 hs-h-35-px hs-min-w-120-px hs-max-w-400-px position-relative mx-1 rounded-2 hs-bg-white hs-border-container-outline-grey global-search-trigger-input"
                placeholder="Search..."
                prefix={
                    <span className="d-inline-flex hs-mr-6 global-search-input-prefix-icon hs-color-placeholder-grey hs-fs-14">
                        {isGlobalSearchDataWasFetching ? <LoadingOutlined spin /> : <SearchOutlined />}
                    </span>
                }
                suffix={
                    <span className="hs-shortcut-background hs-color-dark-grey-2 hs-fs-10-px p-1 rounded-1">
                        {shortcutHintToOpenGlobalSearch}
                    </span>
                }
                value={globalSearchQuery}
                onChange={(e) => handleGlobalSearch(e?.target?.value)}
                onClick={handleOpenGlobalSearch}
                allowClear
            />
        );
    }
);
GlobalSearchInput.displayName = "GlobalSearchInput";

const GlobalSearchBar = ({ isGlobalSearchDropDownOpen, setIsGlobalSearchDropDownOpen }) => {
    const navigate = useNavigate();
    const [currentUrlSearchParams] = useSearchParams();
    const [appProperties] = useContext(AppContext);
    const currentOsyncUserId = appProperties?.currentUser?.osyncUserId;
    const isExtraSmallScreen = Grid.useBreakpoint().xs;
    const {
        doGlobalSearch,
        globalSearchResults,
        isGlobalSearchDataWasFetching,
        setIsGlobalSearchDataWasFetching,
        isGlobalSearchFetchingDataByPagination,
        isMoreDataPresentInPagination,
        loadMoreGlobalSearchResults,
        clearGlobalSearchResults,
    } = useGlobalSearch();
    const [globalSearchQuery, setGlobalSearchQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState(GLOBAL_SEARCH_DEFAULT_ACTIVE_CATEGORY);
    const [globalSearchRecentSearches, setGlobalSearchRecentSearches] = useState(() => getGlobalSearchRecentSearches(currentOsyncUserId));
    const globalSearchInputRef = useRef(null);

    const handleGlobalSearch = (globalSearchQueryValue) => {
        if (!isGlobalSearchDropDownOpen) {
            setIsGlobalSearchDropDownOpen(true);
        }
        setGlobalSearchQuery(globalSearchQueryValue);
        doGlobalSearch(globalSearchQueryValue, activeCategory);
    };

    const handleCloseGlobalSearch = () => {
        setIsGlobalSearchDropDownOpen(false);
        globalSearchInputRef.current?.blur();
        setIsGlobalSearchDataWasFetching(false);
    };

    const handleOpenGlobalSearch = () => {
        globalSearchInputRef.current?.focus();
        setIsGlobalSearchDropDownOpen(true);
        setGlobalSearchRecentSearches(getGlobalSearchRecentSearches(currentOsyncUserId));
    };

    useHotkey("Mod+K", handleOpenGlobalSearch, { enabled: !isExtraSmallScreen });
    useHotkey("Escape", handleCloseGlobalSearch, { enabled: isGlobalSearchDropDownOpen });

    useEffect(() => {
        if (globalSearchQuery?.trim()?.length >= 2 && activeCategory) {
            doGlobalSearch(globalSearchQuery, activeCategory);
        } else if (globalSearchResults?.length > 0) {
            clearGlobalSearchResults();
        }
    }, [activeCategory]);

    const handleGlobalSearchResultClick = useCallback(
        (clickedGlobalSearchResult) => {
            const clickedGlobalSearchResultNavigationPath = getPathForTheSelectedResult(clickedGlobalSearchResult);
            if (clickedGlobalSearchResultNavigationPath) {
                addGlobalSearchRecentSearchIntoLocalStorage(globalSearchQuery, currentOsyncUserId);
                const navigationUrl = clickedGlobalSearchResultNavigationPath + "?" + currentUrlSearchParams.toString();
                const globalSearchNavigationState = getGlobalSearchNavigationState(clickedGlobalSearchResult);
                navigate(navigationUrl, globalSearchNavigationState ? { state: globalSearchNavigationState } : undefined);
                handleCloseGlobalSearch();
            }
        },
        [navigate, globalSearchQuery, currentOsyncUserId, currentUrlSearchParams]
    );

    if (isExtraSmallScreen) return null;

    return (
        <div className="position-relative w-100 hs-max-w-400-px">
            <div className="position-relative">
                <ConfigProvider theme={{
                    components: {
                        Dropdown: {
                            zIndexPopup: 1100,
                        },
                    },
                }}>
                    <Dropdown
                        open={isGlobalSearchDropDownOpen}
                        dropdownRender={() => (
                            <GlobalSearchDropdown
                                appProperties={appProperties}
                                activeCategory={activeCategory}
                                globalSearchResults={globalSearchResults}
                                setActiveCategory={setActiveCategory}
                                clearGlobalSearchResults={clearGlobalSearchResults}
                                setIsGlobalSearchDataWasFetching={setIsGlobalSearchDataWasFetching}
                                globalSearchQuery={globalSearchQuery}
                                isGlobalSearchDataWasFetching={isGlobalSearchDataWasFetching}
                                globalSearchRecentSearches={globalSearchRecentSearches}
                                handleGlobalSearch={handleGlobalSearch}
                                handleGlobalSearchResultClick={handleGlobalSearchResultClick}
                                isMoreDataPresentInPagination={isMoreDataPresentInPagination}
                                isGlobalSearchFetchingDataByPagination={isGlobalSearchFetchingDataByPagination}
                                loadMoreGlobalSearchResults={loadMoreGlobalSearchResults}
                            />
                        )}
                        trigger={[]}
                        overlayClassName="hs-z-index-1100"
                        placement="bottom"
                        align={{ offset: [15, 4] }}
                    >
                        <GlobalSearchInput
                            ref={globalSearchInputRef}
                            isGlobalSearchDataWasFetching={isGlobalSearchDataWasFetching}
                            globalSearchQuery={globalSearchQuery}
                            handleGlobalSearch={handleGlobalSearch}
                            handleOpenGlobalSearch={handleOpenGlobalSearch}
                        />
                    </Dropdown>
                </ConfigProvider>
            </div>
        </div>
    );
};

export default GlobalSearchBar;
